class AnimatedAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = new Delegate(this.element);
    this.announcementBar = document.querySelector('.announcement-bar');
    this.isSticky = this.element.getAttribute('data-sticky');

    this.updateVisibleHeight();
    this.isElementSticky();
    this.transitionSpeed = this.element.getAttribute('data-settings');
    this.animWrapper = this.element.querySelector(
      '.announcement-bar__anim-wrapper'
    );
    this.original = this.element.querySelector(
      '.announcement-bar__duplicate-wrapper'
    );

    this.firstLoad = true;
    this.intersectionObserver = null;
    this._observeIntersection();

    this.shouldAnimate = false;
    this.animationType = null;
    this.wrapperId = null;
    this.swiperInstance = null;
    this.delay = 1000;
  }

  connectedCallback() {
    this.shouldAnimate = this.element.getAttribute('data-animation');
    this.animationType = this.element.getAttribute('data-animation-type');
    this.wrapperId = this.element.getAttribute('data-wrapper-id');
    this.delay = this.element.getAttribute('data-delay');

    if (this.animationType === 'horizontal') {
      this.appendContents();
      this.bindEvents();
    }

    if (this.animationType === 'vertical' && this.shouldAnimate === 'true') {
      this.initSwiper();
    }

    this.updateVisibleHeight();
  }

  initSwiper() {
    if (this.slideCount == 1) {
      console.info('Swiper not initialized. Only one child element.');

      return null;
    }

    const swiperElement = document.getElementById(this.wrapperId);

    // parseFloat: the speed setting steps in 0.5s (MS-004 rotates in 0.5s).
    this.swiperInstance = new Swiper(swiperElement, {
      speed: parseFloat(this.transitionSpeed) * 1000,
      loop: true,
      slidesPerView: 1,
      direction: 'vertical',
      autoplay: {
        // Forward, so the lines read in the order they are set (Black November first).
        reverseDirection: false,
        delay: parseFloat(this.delay) * 1000,
      },
      autoHeight: true,
      preventInteractionOnTransition: true,
    });
    this.bindPause();
  }

  // Reduced motion: no rotation, the first line stays.
  bindPause() {
    if (this.swiperInstance && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.swiperInstance.autoplay.stop();
    }
  }

  destroySwiper() {
    if (this.swiperInstance) {
      try {
        if (this.swiperInstance.destroyed) {
          console.info('Swiper already destroyed');

          this.swiperInstance = null;
          return null;
        }
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      } catch (error) {
        console.error('Error destroying Swiper:', error);
        this.swiperInstance = null; // Fallback
      }
    } else {
      console.log('No Swiper instance to destroy');
    }
  }

  _observeIntersection() {
    // Observe how much of this bar is visible in the viewport
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visibleHeight = Math.round(entry.intersectionRect.height);
          document.documentElement.style.setProperty(
            '--announcement-dynamic-height',
            `${visibleHeight}px`
          );
        });
      },
      {
        root: null,
        threshold: Array.from({ length: 51 }, (_, i) => i / 50),
      }
    );
    this.intersectionObserver.observe(this);
  }

  appendContents() {
    let index = 0;
    let children = Array.from(this.original.children);

    while (children.length < 4 && children.length > 0) {
      this.original.appendChild(children[index].cloneNode(true));
      children = Array.from(this.original.children);
      index++;
    }
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.swiperInstance) {
      this.destroySwiper();
    }
  }

  bindEvents() {
    if (this.shouldAnimate === 'false') {
      return null;
    }

    this.handleBreakpointChange();
  }

  isElementSticky() {
    if (this.isSticky == 'true') {
      this.element
        .closest('.shopify-section')
        .classList.add('announcement--sticky');
    } else {
      this.element
        .closest('.shopify-section')
        .classList.remove('announcement--sticky');
    }
  }

  updateVisibleHeight() {
    const rect = this.element.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    let visibleHeight =
      Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    visibleHeight = Math.max(visibleHeight, 0);

    document.documentElement.style.setProperty(
      '--announcement-dynamic-height',
      `${visibleHeight}px`
    );

    document.documentElement.style.setProperty(
      '--announcement-height-fixed',
      this.element.clientHeight + 'px'
    );
  }

  handleBreakpointChange() {
    if (responsive.matchesBreakpoint('lap_and_up')) {
      this.mobile = false;
      if (this.transitionSpeed) {
        this.duplicateBlockWrappers();
      } else {
        Array.from(this.original.children).forEach(
          (block, i) => (block.style.display = 'block')
        );
        this.original.style.justifyContent = 'space-between';
      }
    } else {
      this.mobile = true;
      if (this.transitionSpeed) {
        this.duplicateBlockWrappers();
      } else {
        Array.from(this.original.children).forEach((block, i) => {
          if (i != 0) block.style.display = 'none';
        });
        this.original.style.justifyContent = 'center';
      }
    }
  }

  resetWrapper() {
    Array.from(this.animWrapper.children).forEach((child, i) => {
      if (i != 0) this.animWrapper.removeChild(this.animWrapper.lastChild);
    });
    this.originalNew =
      this.element.offsetWidth -
      parseInt(getComputedStyle(this.element).paddingLeft) * 2;
  }

  duplicateBlockWrappers() {
    this.resetWrapper();
    let duplicate2 = null;
    let spaceDiv2 = null;

    if (this.original != null) {
      const duplicate = this.original.cloneNode(true),
        spaceDiv = document.createElement('div');
      spaceDiv.style.flexShrink = 0;
      if (this.original.children.length == 1) {
        duplicate2 = duplicate.cloneNode(true);
        spaceDiv2 = spaceDiv.cloneNode(true);
      }

      let originalWidth = this.original.offsetWidth;
      if (this.originalNew) originalWidth = this.originalNew;

      if (this.mobile) {
        if (this.original.children.length >= 2) {
          duplicate.style.width =
            originalWidth * this.original.children.length -
            window.innerWidth +
            'px';
          this.original.style.width =
            originalWidth * this.original.children.length -
            window.innerWidth +
            'px';
          this.animWrapper.style.width =
            2 *
              (originalWidth * this.original.children.length -
                window.innerWidth) +
            'px';
        } else {
          duplicate.style.width = 'fit-content';
          if (duplicate2) duplicate2.style.width = 'fit-content';
          this.original.style.width = 'fit-content';
          const originalWidthAfter = this.original.offsetWidth;
          setTimeout(() => {
            this.animWrapper.style.width = 2 * originalWidthAfter + 400 + 'px';
          }, 0);
        }

        if (this.original.children.length >= 2)
          this.spaceDivWidth = this.getDistanceBetweenBlocks(
            this.original.children[0],
            this.original.children[1]
          );
        else this.spaceDivWidth = 200;
      } else {
        if (this.original.children.length >= 2)
          this.spaceDivWidth = this.getDistanceBetweenBlocks(
            this.original.children[0],
            this.original.children[1]
          );
        else this.spaceDivWidth = 0;

        duplicate.style.width = originalWidth + 'px';
        this.original.style.width = originalWidth + 'px';
        this.animWrapper.style.width =
          2 * originalWidth + this.spaceDivWidth + 'px';
      }

      spaceDiv.style.width = this.spaceDivWidth + 'px';
      if (spaceDiv2) {
        spaceDiv2.style.width = this.spaceDivWidth + 'px';
      }

      this.animWrapper.style.position = 'absolute';
      if (this.mobile && this.firstLoad)
        this.animWrapper.style.transform = `translateX(${
          this.spaceDivWidth / 2
        }px)`;

      this.element.style.height = this.animWrapper.offsetHeight + 'px';

      this.animWrapper.appendChild(spaceDiv);
      this.animWrapper.appendChild(duplicate);
      if (this.original.children.length == 1 && this.mobile) {
        this.animWrapper.appendChild(spaceDiv2);
        this.animWrapper.appendChild(duplicate2);
      }

      setTimeout(() => {
        this.startAnimating();
      }, 100);
    }
  }

  startAnimating() {
    let translate = 0;
    this.mobile && this.firstLoad
      ? (translate = this.spaceDivWidth / 2)
      : (translate = 0);
    this.firstLoad = false;

    this.animWrapper.style.setProperty(
      '--marquee-x-start-position',
      `${translate}px`
    );

    this.animWrapper.style.setProperty(
      '--marquee-x-end-position',
      `calc(-50% - ${this.spaceDivWidth / 2}px)`
    );
  }

  getDistanceBetweenBlocks(blockOne, blockTwo) {
    return (
      blockTwo.getBoundingClientRect().left -
      blockOne.getBoundingClientRect().right
    );
  }
}

customElements.define('announcement-bar', AnimatedAnnouncementBar);
